/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import styled from "styled-components";
import Button from "./Button";
import Icon from "./Icon";
import { useTranslate } from "../../hooks/useTranslate";

const StyledPaginator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

const PaginationButton = ({ onClick, disabled, icon, title, isActive, ariaLabel }) => (
  <Button
    variant="outline"
    size="icon"
    onClick={onClick}
    disabled={disabled}
    icon={icon}
    title={title}
    isActive={isActive}
    aria-label={ariaLabel}
    aria-current={isActive ? "page" : undefined}
  />
);

const Ellipsis = () => <Button variant="outline" size="icon" disabled={true} title="..." aria-hidden="true" />;

const Paginator = ({ currentPage, totalPages, onPageChange, isLoading }) => {
  const { _t } = useTranslate();
  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;

  const showAllPages = totalPages <= 5;
  const showFirstEllipsis = !showAllPages && currentPage > 3;
  const showLastEllipsis = !showAllPages && currentPage < totalPages - 2;

  const middlePages = showAllPages
    ? Array.from({ length: Math.max(0, totalPages - 2) }, (_, i) => i + 2)
    : [Math.max(2, currentPage - 1), currentPage, Math.min(totalPages - 1, currentPage + 1)].filter(
      (page, index, arr) => page > 1 && page < totalPages && arr.indexOf(page) === index
    );

  return (
    <StyledPaginator>
      <PaginationButton
        onClick={() => onPageChange(currentPage - 1)}
        disabled={isFirstPage || isLoading}
        icon={<Icon.ChevronLeft size={Icon.SIZE.SM} />}
        ariaLabel={_t("Previous page")}
      />

      <PaginationButton onClick={() => onPageChange(1)} isActive={isFirstPage} title={1} />

      {showFirstEllipsis && <Ellipsis />}

      {middlePages.map((page) => (
        <PaginationButton
          key={page}
          onClick={() => onPageChange(page)}
          isActive={currentPage === page}
          title={page}
        />
      ))}

      {showLastEllipsis && <Ellipsis />}

      {totalPages > 1 && (
        <PaginationButton onClick={() => onPageChange(totalPages)} isActive={isLastPage} title={totalPages} />
      )}

      <PaginationButton
        onClick={() => onPageChange(currentPage + 1)}
        disabled={isLastPage || isLoading}
        icon={<Icon.ChevronRight size={Icon.SIZE.SM} />}
        ariaLabel={_t("Next page")}
      />
    </StyledPaginator>
  );
};

export default Paginator;
